import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Switch,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import {
  Add as AddIcon,
  MonetizationOn as CoinIcon,
  CurrencyRupee as RupeeIcon,
  Redeem as RedeemIcon,
  Group as UsersIcon,
  TrendingUp as TrendUpIcon,
  TrendingDown as TrendDownIcon,
  Edit as EditIcon,
  MoreHoriz as MoreIcon,
  Close as CloseIcon,
  ContentCopy as DuplicateIcon,
  Block as DeactivateIcon,
  CheckCircleOutline as ActivateIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';

// ─── Theme color (matching SubscriptionManagement) ───
const ACCENT = '#6D53F4';
const ACCENT_HOVER = '#5A3FE0';
const ACCENT_LIGHT = '#F0EDFE';

// ─── Sub-tab definitions ───
const SUB_TABS = ['Coin Packages', 'Earning Tasks', 'Rewards', 'Wallet Analytics', 'Settings'];

// ─── Country / Currency data ───
const COUNTRIES = ['India', 'United States', 'United Kingdom', 'Canada', 'Australia'];
const CURRENCY_MAP = {
  India: 'INR (₹)',
  'United States': 'USD ($)',
  'United Kingdom': 'GBP (£)',
  Canada: 'CAD ($)',
  Australia: 'AUD ($)',
};

// ─── Stats data ───
const INITIAL_STATS = [
  {
    label: 'Total Coins\nCirculating',
    value: '1,200,000',
    change: '+8.2%',
    positive: true,
    icon: <CoinIcon />,
    iconBg: '#EDE9FE',
    iconColor: ACCENT,
  },
  {
    label: 'Total Revenue',
    value: '₹12,50,000',
    change: '+14.5%',
    positive: true,
    icon: <RupeeIcon />,
    iconBg: '#EDE9FE',
    iconColor: ACCENT,
  },
  {
    label: 'Coins Redeemed',
    value: '450,000',
    change: '+3.1%',
    positive: true,
    icon: <RedeemIcon />,
    iconBg: '#F0FDF4',
    iconColor: '#16A34A',
  },
  {
    label: 'Active Wallet Users',
    value: '24,500',
    change: '-1.2%',
    positive: false,
    icon: <UsersIcon />,
    iconBg: '#EDE9FE',
    iconColor: ACCENT,
  },
];

// ─── Earning Tasks state managed below ───



// ─── Consumable Purchases fetched from API ───

// ─── Mock Rewards ───
const mockRewards = [
  {
    id: 1,
    name: 'Profile Verification',
    type: 'Free Connect',
    value: '5 User Count',
    usersEarned: '4,250',
    status: 'Active',
  },
  {
    id: 2,
    name: 'Profile Verification',
    type: 'LBK coins',
    value: '0 Coin Count',
    usersEarned: '4,250',
    status: 'Active',
  },
  {
    id: 3,
    name: 'Profile Verification',
    type: 'Free Post',
    value: '5 Post Count',
    usersEarned: '4,250',
    status: 'Active',
  },
  {
    id: 4,
    name: 'Profile Verification',
    type: 'Top Profile',
    value: '5 Days Count',
    usersEarned: '4,250',
    status: 'Active',
  },
  {
    id: 5,
    name: 'Profile Verification',
    type: 'Top Post Listing',
    value: '5 Days Count',
    usersEarned: '4,250',
    status: 'Active',
  },
  {
    id: 6,
    name: 'Profile Verification',
    type: 'Who Likes You',
    value: '5 User Count',
    usersEarned: '4,250',
    status: 'Active',
  },
  {
    id: 7,
    name: 'Onboarding Process',
    type: 'Super Like',
    value: '5 User Count',
    usersEarned: '2,100',
    status: 'Active',
  },
];

// ─── Shared table header style ───
const thStyle = {
  fontWeight: 600,
  color: '#6B7280',
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  borderBottom: '1px solid #E5E7EB',
  bgcolor: '#FAFAFA',
  py: 1.5,
};

const LBKWalletManagement = () => {
  const [activeTab, setActiveTab] = useState('Coin Packages');
  const [country, setCountry] = useState('India');
  const [currency, setCurrency] = useState('INR (₹)');
  const [stats, setStats] = useState(INITIAL_STATS);
  const [chartData, setChartData] = useState({
    packageStats: [],
    monthlyStats: [],
    dailyActiveUsers: []
  });
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  // ─── Fetch Analytics Data ───
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const { data } = await axios.get('/analytics/coin-circulation');
        setStats((prev) => {
          const newStats = prev.map(stat => ({ ...stat }));
          // Update Total Coins Circulating
          newStats[0].value = data.totalCirculating.toLocaleString();
          newStats[0].change = `${data.circulatingGrowth >= 0 ? '+' : ''}${data.circulatingGrowth}%`;
          newStats[0].positive = data.circulatingGrowth >= 0;
          
          // Update Total Revenue
          if (data.lifetimeRevenue !== undefined) {
            newStats[1].value = `₹${data.lifetimeRevenue.toLocaleString('en-IN')}`;
            newStats[1].change = `${data.revenueGrowth >= 0 ? '+' : ''}${data.revenueGrowth}%`;
            newStats[1].positive = data.revenueGrowth >= 0;
          }

          // Update Coins Redeemed
          newStats[2].value = data.totalCoinsRedeemed.toLocaleString();
          newStats[2].change = `${data.redeemedGrowth >= 0 ? '+' : ''}${data.redeemedGrowth}%`;
          newStats[2].positive = data.redeemedGrowth >= 0;

          // Update Active Wallet Users
          if (data.activeWalletUsers !== undefined) {
            newStats[3].value = data.activeWalletUsers.toLocaleString();
            newStats[3].change = `${data.activeUsersGrowth >= 0 ? '+' : ''}${data.activeUsersGrowth}%`;
            newStats[3].positive = data.activeUsersGrowth >= 0;
          }

          return newStats;
        });

        // Fetch wallet charts data
        const { data: chartDataRes } = await axios.get('/analytics/wallet-charts');
        setChartData(chartDataRes);

      } catch (error) {
        console.error('Failed to fetch coin circulation stats', error);
      }
    };
    fetchAnalytics();
  }, []);

  // ─── Coin Packages & Consumables State ───
  const [coinPackages, setCoinPackages] = useState([]);
  const [loadingCoinPacks, setLoadingCoinPacks] = useState(false);

  // ─── Fetch Coin Packages from API ───
  const fetchCoinPacks = async () => {
    try {
      setLoadingCoinPacks(true);
      const { data } = await axios.get('/admin/razorpay/credit-packs');
      if (data.success && data.coinPacks) {
        const mapped = data.coinPacks.map((pack) => ({
          id: pack._id,
          name: pack.name,
          coins: pack.coins.toLocaleString(),
          price: `₹${(pack.price / 100).toLocaleString()}`,
          bonusCoins: pack.bonusCoins > 0 ? pack.bonusCoins.toLocaleString() : '—',
          popularity: pack.popularity || 'Normal',
          status: pack.isActive ? 'Active' : 'Inactive',
        }));
        setCoinPackages(mapped);
      }
    } catch (error) {
      console.error('Failed to fetch coin packs:', error);
    } finally {
      setLoadingCoinPacks(false);
    }
  };

  useEffect(() => {
    fetchCoinPacks();
    fetchEarningTasks();
    fetchWalletSettings();
    fetchRewards();
    fetchConsumables();
  }, []);

  // ─── Earning Tasks State ───
  const [earningTasks, setEarningTasks] = useState([]);
  const [loadingEarningTasks, setLoadingEarningTasks] = useState(false);

  // Modals state for Earning Tasks
  const [openAddTaskModal, setOpenAddTaskModal] = useState(false);
  const [openEditTaskModal, setOpenEditTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  // Earning Task Form State
  const [taskName, setTaskName] = useState('');
  const [taskType, setTaskType] = useState('');
  const [taskReward, setTaskReward] = useState('');
  const [taskRequirement, setTaskRequirement] = useState('');
  const [taskTargetCount, setTaskTargetCount] = useState('1');
  const [taskStatus, setTaskStatus] = useState('Active');

  // Fetch Earning Tasks
  const fetchEarningTasks = async () => {
    try {
      setLoadingEarningTasks(true);
      const { data } = await axios.get('/admin/razorpay/earning-tasks');
      if (data.success && data.tasks) {
        setEarningTasks(data.tasks);
      }
    } catch (error) {
      console.error('Failed to fetch earning tasks:', error);
    } finally {
      setLoadingEarningTasks(false);
    }
  };

  const handleOpenEditTask = (task) => {
    setEditingTask(task);
    setTaskName(task.name);
    setTaskType(task.taskType);
    setTaskReward(task.rewardCoins.toString());
    setTaskRequirement(task.requirement);
    setTaskTargetCount(task.targetCount.toString());
    setTaskStatus(task.status);
    setOpenEditTaskModal(true);
  };

  const handleCloseTaskModal = () => {
    setOpenAddTaskModal(false);
    setOpenEditTaskModal(false);
    setEditingTask(null);
    setTaskName('');
    setTaskType('');
    setTaskReward('');
    setTaskRequirement('');
    setTaskTargetCount('1');
    setTaskStatus('Active');
  };

  const handleSaveTask = async () => {
    if (!taskName || !taskType || !taskReward || !taskRequirement) {
      alert('Please fill out required fields');
      return;
    }

    try {
      if (editingTask) {
        await axios.put(`/admin/razorpay/earning-tasks/${editingTask._id}`, {
          name: taskName,
          taskType,
          rewardCoins: Number(taskReward),
          requirement: taskRequirement,
          targetCount: Number(taskTargetCount),
          status: taskStatus,
        });
      } else {
        await axios.post('/admin/razorpay/earning-tasks', {
          name: taskName,
          taskType,
          rewardCoins: Number(taskReward),
          requirement: taskRequirement,
          targetCount: Number(taskTargetCount),
          status: taskStatus,
        });
      }
      await fetchEarningTasks();
      handleCloseTaskModal();
    } catch (error) {
      console.error('Failed to save earning task:', error);
      alert(error.response?.data?.message || 'Failed to save earning task');
    }
  };

  // Earning Task Context Menu
  const [taskMenuAnchor, setTaskMenuAnchor] = useState(null);
  const [menuTask, setMenuTask] = useState(null);
  const [openDeleteTaskDialog, setOpenDeleteTaskDialog] = useState(false);
  const [deletingTask, setDeletingTask] = useState(null);

  const handleOpenTaskMenu = (event, task) => {
    setTaskMenuAnchor(event.currentTarget);
    setMenuTask(task);
  };

  const handleCloseTaskMenu = () => {
    setTaskMenuAnchor(null);
  };

  const handleToggleTaskStatus = async () => {
    if (menuTask) {
      const targetId = menuTask._id;
      const newStatus = menuTask.status === 'Active' ? 'Inactive' : 'Active';

      setEarningTasks((prev) =>
        prev.map((t) => (t._id === targetId ? { ...t, status: newStatus } : t))
      );
      handleCloseTaskMenu();
      setMenuTask(null);

      try {
        await axios.put(`/admin/razorpay/earning-tasks/${targetId}`, {
          status: newStatus,
        });
      } catch (error) {
        console.error('Failed to toggle task status:', error);
        alert(error.response?.data?.message || 'Failed to update task status');
        await fetchEarningTasks();
      }
    } else {
      handleCloseTaskMenu();
      setMenuTask(null);
    }
  };

  const handleDeleteTaskClick = () => {
    setDeletingTask(menuTask);
    setOpenDeleteTaskDialog(true);
    setTaskMenuAnchor(null);
  };

  const handleConfirmDeleteTask = async () => {
    if (deletingTask) {
      try {
        await axios.delete(`/admin/razorpay/earning-tasks/${deletingTask._id}`);
        setEarningTasks((prev) => prev.filter((t) => t._id !== deletingTask._id));
      } catch (error) {
        console.error('Failed to delete task:', error);
        alert(error.response?.data?.message || 'Failed to delete task');
      }
    }
    setOpenDeleteTaskDialog(false);
    setDeletingTask(null);
    setMenuTask(null);
  };

  const handleCancelDeleteTask = () => {
    setOpenDeleteTaskDialog(false);
    setDeletingTask(null);
    setMenuTask(null);
  };
  const [consumables, setConsumables] = useState([]);
  const [loadingConsumables, setLoadingConsumables] = useState(false);

  // Modals state for Coin Packages
  const [openAddPackageModal, setOpenAddPackageModal] = useState(false);
  const [openEditPackageModal, setOpenEditPackageModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  
  // Package Form State
  const [pkgName, setPkgName] = useState('');
  const [pkgCoins, setPkgCoins] = useState('');
  const [pkgPrice, setPkgPrice] = useState('');
  const [pkgBonus, setPkgBonus] = useState('');
  const [pkgPopularity, setPkgPopularity] = useState('');
  const [pkgStatus, setPkgStatus] = useState('Active');

  const handleOpenEditPackage = (pkg) => {
    setEditingPackage(pkg);
    setPkgName(pkg.name);
    setPkgCoins(pkg.coins.replace(/,/g, ''));
    setPkgPrice(pkg.price);
    setPkgBonus(pkg.bonusCoins === '—' ? '0' : pkg.bonusCoins.replace(/,/g, ''));
    setPkgPopularity(pkg.popularity);
    setPkgStatus(pkg.status);
    setOpenEditPackageModal(true);
  };

  const handleClosePackageModal = () => {
    setOpenAddPackageModal(false);
    setOpenEditPackageModal(false);
    setEditingPackage(null);
    setPkgName('');
    setPkgCoins('');
    setPkgPrice('');
    setPkgBonus('');
    setPkgPopularity('');
    setPkgStatus('Active');
  };

  const handleSavePackage = async () => {
    if (!pkgName || !pkgCoins || !pkgPrice) {
      alert('Please fill out required fields');
      return;
    }

    try {
      if (editingPackage) {
        // Edit existing package — local update for now
        const formattedCoins = Number(pkgCoins).toLocaleString();
        const formattedBonus = pkgBonus && pkgBonus !== '0' ? Number(pkgBonus).toLocaleString() : '—';
        setCoinPackages(coinPackages.map(p => p.id === editingPackage.id ? {
          ...p, name: pkgName, coins: formattedCoins, price: pkgPrice, bonusCoins: formattedBonus, popularity: pkgPopularity, status: pkgStatus
        } : p));
      } else {
        // Create new package via API
        const priceInPaise = Math.round(Number(pkgPrice) * 100);
        await axios.post('/admin/razorpay/credit-packs', {
          name: pkgName,
          coins: Number(pkgCoins),
          price: priceInPaise,
          bonusCoins: pkgBonus ? Number(pkgBonus) : 0,
          popularity: pkgPopularity || 'Normal',
        });
        // Re-fetch from DB to get the latest list
        await fetchCoinPacks();
      }
      handleClosePackageModal();
    } catch (error) {
      console.error('Failed to save coin pack:', error);
      alert(error.response?.data?.message || 'Failed to save coin pack');
    }
  };

  // ─── Coin Package context menu state ───
  const [pkgMenuAnchor, setPkgMenuAnchor] = useState(null);
  const [menuPackage, setMenuPackage] = useState(null);
  const [openDeletePkgDialog, setOpenDeletePkgDialog] = useState(false);
  const [deletingPackage, setDeletingPackage] = useState(null);

  const handleOpenPkgMenu = (event, pkg) => {
    setPkgMenuAnchor(event.currentTarget);
    setMenuPackage(pkg);
  };

  const handleClosePkgMenu = () => {
    setPkgMenuAnchor(null);
  };

  const handleDuplicatePackage = async () => {
    if (menuPackage) {
      try {
        // Parse values back from formatted display strings
        const coins = Number(menuPackage.coins.replace(/,/g, ''));
        const priceNum = Number(menuPackage.price.replace(/[₹,]/g, ''));
        const priceInPaise = Math.round(priceNum * 100);
        const bonus = menuPackage.bonusCoins === '—' ? 0 : Number(menuPackage.bonusCoins.replace(/,/g, ''));

        await axios.post('/admin/razorpay/credit-packs', {
          name: `${menuPackage.name} (Copy)`,
          coins,
          price: priceInPaise,
          bonusCoins: bonus,
          popularity: menuPackage.popularity || 'Normal',
        });
        await fetchCoinPacks();
      } catch (error) {
        console.error('Failed to duplicate coin pack:', error);
        alert(error.response?.data?.message || 'Failed to duplicate coin pack');
      }
    }
    handleClosePkgMenu();
    setMenuPackage(null);
  };

  const handleTogglePackageStatus = async () => {
    if (menuPackage) {
      const targetId = menuPackage.id;
      const newStatusLabel = menuPackage.status === 'Active' ? 'Inactive' : 'Active';
      const newIsActive = menuPackage.status !== 'Active';

      // Optimistic UI update
      setCoinPackages((prev) =>
        prev.map((p) => (p.id === targetId ? { ...p, status: newStatusLabel } : p))
      );
      handleClosePkgMenu();
      setMenuPackage(null);

      try {
        await axios.put(`/admin/razorpay/credit-packs/${targetId}`, {
          isActive: newIsActive,
        });
      } catch (error) {
        console.error('Failed to toggle coin pack status:', error);
        alert(error.response?.data?.message || 'Failed to update coin pack status');
        // Revert on failure
        await fetchCoinPacks();
      }
    } else {
      handleClosePkgMenu();
      setMenuPackage(null);
    }
  };

  const handleDeletePackageClick = () => {
    setDeletingPackage(menuPackage);
    setOpenDeletePkgDialog(true);
    setPkgMenuAnchor(null);
  };

  const handleConfirmDeletePackage = async () => {
    if (deletingPackage) {
      try {
        await axios.delete(`/admin/razorpay/credit-packs/${deletingPackage.id}`);
        // Remove from local state immediately
        setCoinPackages((prev) => prev.filter((p) => p.id !== deletingPackage.id));
      } catch (error) {
        console.error('Failed to delete coin pack:', error);
        alert(error.response?.data?.message || 'Failed to delete coin pack');
      }
    }
    setOpenDeletePkgDialog(false);
    setDeletingPackage(null);
    setMenuPackage(null);
  };

  const handleCancelDeletePackage = () => {
    setOpenDeletePkgDialog(false);
    setDeletingPackage(null);
    setMenuPackage(null);
  };

  // ─── Fetch Consumables from API ───
  const fetchConsumables = async () => {
    try {
      setLoadingConsumables(true);
      const { data } = await axios.get('/admin/razorpay/consumables');
      if (data.success && data.consumables) {
        setConsumables(data.consumables);
      }
    } catch (error) {
      console.error('Failed to fetch consumables:', error);
    } finally {
      setLoadingConsumables(false);
    }
  };

  // Modals state for Consumables
  const [openAddConsumableModal, setOpenAddConsumableModal] = useState(false);
  const [openEditConsumableModal, setOpenEditConsumableModal] = useState(false);
  const [editingConsumable, setEditingConsumable] = useState(null);

  // Consumable Form State
  const [consName, setConsName] = useState('');
  const [consItemType, setConsItemType] = useState('');
  const [consCoins, setConsCoins] = useState('');
  const [consDesc, setConsDesc] = useState('');
  const [consStatus, setConsStatus] = useState('Active');

  // Consumable delete confirmation dialog
  const [openDeleteConsumableDialog, setOpenDeleteConsumableDialog] = useState(false);
  const [deletingConsumable, setDeletingConsumable] = useState(null);

  const handleOpenEditConsumable = (cons) => {
    setEditingConsumable(cons);
    setConsName(cons.name);
    setConsItemType(cons.itemType || '');
    setConsCoins(cons.coinsRequired.toString());
    setConsDesc(cons.description);
    setConsStatus(cons.status);
    setOpenEditConsumableModal(true);
  };

  const handleCloseConsumableModal = () => {
    setOpenAddConsumableModal(false);
    setOpenEditConsumableModal(false);
    setEditingConsumable(null);
    setConsName('');
    setConsItemType('');
    setConsCoins('');
    setConsDesc('');
    setConsStatus('Active');
  };

  const handleSaveConsumable = async () => {
    if (!consName || !consCoins || !consItemType) {
      alert('Please fill out required fields (Name, Item Type, Coins Required)');
      return;
    }
    try {
      if (editingConsumable) {
        await axios.put(`/admin/razorpay/consumables/${editingConsumable._id}`, {
          name: consName,
          itemType: consItemType,
          coinsRequired: Number(consCoins),
          description: consDesc,
          status: consStatus,
        });
      } else {
        await axios.post('/admin/razorpay/consumables', {
          name: consName,
          itemType: consItemType,
          coinsRequired: Number(consCoins),
          description: consDesc,
          status: consStatus,
        });
      }
      await fetchConsumables();
      handleCloseConsumableModal();
    } catch (error) {
      console.error('Failed to save consumable:', error);
      alert(error.response?.data?.message || 'Failed to save consumable');
    }
  };

  const handleToggleConsumableStatus = async (cons) => {
    const newStatus = cons.status === 'Active' ? 'Disabled' : 'Active';
    // Optimistic update
    setConsumables(prev => prev.map(c => c._id === cons._id ? { ...c, status: newStatus } : c));
    try {
      await axios.put(`/admin/razorpay/consumables/${cons._id}`, { status: newStatus });
    } catch (error) {
      console.error('Failed to toggle consumable status:', error);
      alert(error.response?.data?.message || 'Failed to update status');
      await fetchConsumables();
    }
  };

  const handleDeleteConsumableClick = (cons) => {
    setDeletingConsumable(cons);
    setOpenDeleteConsumableDialog(true);
  };

  const handleConfirmDeleteConsumable = async () => {
    if (deletingConsumable) {
      try {
        await axios.delete(`/admin/razorpay/consumables/${deletingConsumable._id}`);
        setConsumables(prev => prev.filter(c => c._id !== deletingConsumable._id));
      } catch (error) {
        console.error('Failed to delete consumable:', error);
        alert(error.response?.data?.message || 'Failed to delete consumable');
      }
    }
    setOpenDeleteConsumableDialog(false);
    setDeletingConsumable(null);
  };

  const handleCancelDeleteConsumable = () => {
    setOpenDeleteConsumableDialog(false);
    setDeletingConsumable(null);
  };


  const [openAddRewardModal, setOpenAddRewardModal] = useState(false);
  const [newRewardName, setNewRewardName] = useState('');
  const [newRewardDesc, setNewRewardDesc] = useState('');
  const [newRewardType, setNewRewardType] = useState('');
  const [newRewardValue, setNewRewardValue] = useState('');
  const [newRewardUnit, setNewRewardUnit] = useState('Count');

  // ─── Rewards state ───
  const [rewards, setRewards] = useState([]);
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [newRewardStatus, setNewRewardStatus] = useState('Active');

  const fetchRewards = async () => {
    try {
      setLoadingRewards(true);
      const { data } = await axios.get('/admin/razorpay/rewards');
      if (data.success && data.rewards) {
        setRewards(data.rewards);
      }
    } catch (error) {
      console.error('Failed to fetch rewards:', error);
    } finally {
      setLoadingRewards(false);
    }
  };

  const handleAddRewardSubmit = async () => {
    if (!newRewardName.trim() || !newRewardValue.trim() || !newRewardType) {
      alert('Please fill out all required fields.');
      return;
    }
    
    try {
      await axios.post('/admin/razorpay/rewards', {
        name: newRewardName,
        description: newRewardDesc,
        rewardType: newRewardType,
        value: Number(newRewardValue),
        unit: newRewardUnit,
        status: newRewardStatus,
      });
      await fetchRewards();
      handleCloseRewardModal();
    } catch (error) {
      console.error('Failed to add reward:', error);
      alert(error.response?.data?.message || 'Failed to add reward');
    }
  };

  const handleCloseRewardModal = () => {
    setNewRewardName('');
    setNewRewardDesc('');
    setNewRewardType('');
    setNewRewardValue('');
    setNewRewardUnit('Count');
    setNewRewardStatus('Active');
    setOpenAddRewardModal(false);
  };

  // ─── Edit Reward state ───
  const [openEditRewardModal, setOpenEditRewardModal] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [editRewardName, setEditRewardName] = useState('');
  const [editRewardDesc, setEditRewardDesc] = useState('');
  const [editRewardType, setEditRewardType] = useState('');
  const [editRewardValue, setEditRewardValue] = useState('');
  const [editRewardStatus, setEditRewardStatus] = useState('Active');

  const handleOpenEditRewardModal = (reward) => {
    setEditingReward(reward);
    setEditRewardName(reward.name);
    setEditRewardDesc(reward.description || '');
    setEditRewardType(reward.rewardType);
    setEditRewardValue(reward.value.toString());
    setEditRewardUnit(reward.unit || 'Count');
    setEditRewardStatus(reward.status);
    setOpenEditRewardModal(true);
  };

  const handleEditRewardSubmit = async () => {
    if (!editRewardName.trim() || !editRewardValue.trim() || !editRewardType) {
      alert('Please fill out all required fields.');
      return;
    }
    try {
      await axios.put(`/admin/razorpay/rewards/${editingReward._id}`, {
        name: editRewardName,
        description: editRewardDesc,
        rewardType: editRewardType,
        value: Number(editRewardValue),
        unit: editRewardUnit,
        status: editRewardStatus,
      });
      await fetchRewards();
      handleCloseEditRewardModal();
    } catch (error) {
      console.error('Failed to update reward:', error);
      alert(error.response?.data?.message || 'Failed to update reward');
    }
  };

  const [editRewardUnit, setEditRewardUnit] = useState('Count');

  const handleCloseEditRewardModal = () => {
    setEditingReward(null);
    setEditRewardName('');
    setEditRewardDesc('');
    setEditRewardType('');
    setEditRewardValue('');
    setEditRewardUnit('Count');
    setEditRewardStatus('Active');
    setOpenEditRewardModal(false);
  };

  // ─── Reward context menu state ───
  const [rewardMenuAnchor, setRewardMenuAnchor] = useState(null);
  const [menuReward, setMenuReward] = useState(null);

  const handleOpenRewardMenu = (event, reward) => {
    setRewardMenuAnchor(event.currentTarget);
    setMenuReward(reward);
  };

  const handleCloseRewardMenu = () => {
    setRewardMenuAnchor(null);
    setMenuReward(null);
  };

  const [openDeleteRewardDialog, setOpenDeleteRewardDialog] = useState(false);
  const [deletingReward, setDeletingReward] = useState(null);

  const handleDuplicateReward = async () => {
    if (menuReward) {
      try {
        await axios.post('/admin/razorpay/rewards', {
          name: `${menuReward.name} (Copy)`,
          description: menuReward.description || '',
          rewardType: menuReward.rewardType,
          value: menuReward.value,
          unit: menuReward.unit,
          status: 'Inactive',
        });
        await fetchRewards();
      } catch (error) {
        console.error('Failed to duplicate reward:', error);
        alert('Failed to duplicate reward');
      }
    }
    handleCloseRewardMenu();
  };

  const handleToggleRewardStatus = async () => {
    if (menuReward) {
      const newStatus = menuReward.status === 'Active' ? 'Inactive' : 'Active';
      
      // Optimistic update
      setRewards((prev) => prev.map((r) => r._id === menuReward._id ? { ...r, status: newStatus } : r));
      
      try {
        await axios.put(`/admin/razorpay/rewards/${menuReward._id}`, { status: newStatus });
      } catch (error) {
        console.error('Failed to toggle status:', error);
        alert('Failed to toggle reward status');
        await fetchRewards();
      }
    }
    handleCloseRewardMenu();
  };

  const handleDeleteRewardClick = () => {
    setDeletingReward(menuReward);
    setOpenDeleteRewardDialog(true);
    setRewardMenuAnchor(null);
  };

  const handleConfirmDeleteReward = async () => {
    if (deletingReward) {
      try {
        await axios.delete(`/admin/razorpay/rewards/${deletingReward._id}`);
        setRewards(rewards.filter((r) => r._id !== deletingReward._id));
      } catch (error) {
        console.error('Failed to delete reward:', error);
        alert(error.response?.data?.message || 'Failed to delete reward');
      }
    }
    setOpenDeleteRewardDialog(false);
    setDeletingReward(null);
    setMenuReward(null);
  };

  const handleCancelDeleteReward = () => {
    setOpenDeleteRewardDialog(false);
    setDeletingReward(null);
    setMenuReward(null);
  };

  // ─── Settings state ───
  const [defaultWelcomeCoins, setDefaultWelcomeCoins] = useState('50');
  const [dailyLoginReward, setDailyLoginReward] = useState('5');
  const [maxCoinBalance, setMaxCoinBalance] = useState('100000');
  const [coinExpiryDuration, setCoinExpiryDuration] = useState('365');
  const [enableCoinPurchases, setEnableCoinPurchases] = useState(false);
  const [enableRewardTasks, setEnableRewardTasks] = useState(true);
  const [enableConsumablePurchases, setEnableConsumablePurchases] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchWalletSettings = async () => {
    try {
      const { data } = await axios.get('/admin/razorpay/wallet-settings');
      if (data.success && data.settings) {
        setDefaultWelcomeCoins(data.settings.defaultWelcomeCoins.toString());
        setDailyLoginReward(data.settings.dailyLoginReward.toString());
        setMaxCoinBalance(data.settings.maxCoinBalance.toString());
        setCoinExpiryDuration(data.settings.coinExpiryDuration.toString());
        setEnableCoinPurchases(data.settings.enableCoinPurchases);
        setEnableRewardTasks(data.settings.enableRewardTasks);
        setEnableConsumablePurchases(data.settings.enableConsumablePurchases);
      }
    } catch (error) {
      console.error('Failed to fetch wallet settings:', error);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      await axios.put('/admin/razorpay/wallet-settings', {
        defaultWelcomeCoins: Number(defaultWelcomeCoins),
        dailyLoginReward: Number(dailyLoginReward),
        maxCoinBalance: Number(maxCoinBalance),
        coinExpiryDuration: Number(coinExpiryDuration),
        enableCoinPurchases,
        enableRewardTasks,
        enableConsumablePurchases,
      });
      alert('Wallet settings saved successfully!');
    } catch (error) {
      console.error('Failed to save wallet settings:', error);
      alert('Failed to save wallet settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  // Handle country change → auto-fill currency
  const handleCountryChange = (e) => {
    const selectedCountry = e.target.value;
    setCountry(selectedCountry);
    setCurrency(CURRENCY_MAP[selectedCountry] || 'INR (₹)');
  };

  // ─── Render Settings Tab ───
  const renderSettingsTab = () => (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      {/* Section Header */}
      <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #F3F4F6' }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}
        >
          Wallet Settings
        </Typography>
        <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
          Configure global LBK Wallet behavior.
        </Typography>
      </Box>

      {/* Coin Defaults */}
      <Box sx={{ px: 3, py: 3 }}>
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '0.08em',
            color: '#6B7280',
            mb: 2.5,
            display: 'block',
          }}
        >
          COIN DEFAULTS
        </Typography>

        {/* Row 1: Default Welcome Coins & Daily Login Reward */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: '#111827', mb: 0.8, fontSize: '0.875rem' }}
            >
              Default Welcome Coins
            </Typography>
            <TextField
              fullWidth
              value={defaultWelcomeCoins}
              onChange={(e) => setDefaultWelcomeCoins(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
              }}
            />
            <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>
              Coins given to new users on signup.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: '#111827', mb: 0.8, fontSize: '0.875rem' }}
            >
              Daily Login Reward
            </Typography>
            <TextField
              fullWidth
              value={dailyLoginReward}
              onChange={(e) => setDailyLoginReward(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
              }}
            />
            <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>
              Coins awarded per daily login.
            </Typography>
          </Box>
        </Box>

        {/* Row 2: Maximum Coin Balance & Coin Expiry Duration */}
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: '#111827', mb: 0.8, fontSize: '0.875rem' }}
            >
              Maximum Coin Balance
            </Typography>
            <TextField
              fullWidth
              value={maxCoinBalance}
              onChange={(e) => setMaxCoinBalance(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
              }}
            />
            <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>
              Maximum coins a user can hold.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: '#111827', mb: 0.8, fontSize: '0.875rem' }}
            >
              Coin Expiry Duration (days)
            </Typography>
            <TextField
              fullWidth
              value={coinExpiryDuration}
              onChange={(e) => setCoinExpiryDuration(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
              }}
            />
            <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>
              Set 0 for no expiry.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ borderTop: '1px solid #F3F4F6' }} />

      {/* Feature Toggles */}
      <Box sx={{ px: 3, py: 3 }}>
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '0.08em',
            color: '#6B7280',
            mb: 2.5,
            display: 'block',
          }}
        >
          FEATURE TOGGLES
        </Typography>

        {/* Enable Coin Purchases */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2.5,
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem' }}
            >
              Enable Coin Purchases
            </Typography>
            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
              Allow users to purchase coin packages.
            </Typography>
          </Box>
          <Switch
            checked={enableCoinPurchases}
            onChange={() => setEnableCoinPurchases(!enableCoinPurchases)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: ACCENT,
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                bgcolor: ACCENT,
              },
            }}
          />
        </Box>

        {/* Enable Reward Tasks */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2.5,
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem' }}
            >
              Enable Reward Tasks
            </Typography>
            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
              Allow users to earn coins via tasks.
            </Typography>
          </Box>
          <Switch
            checked={enableRewardTasks}
            onChange={() => setEnableRewardTasks(!enableRewardTasks)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: ACCENT,
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                bgcolor: ACCENT,
              },
            }}
          />
        </Box>

        {/* Enable Consumable Purchases */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem' }}
            >
              Enable Consumable Purchases
            </Typography>
            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
              Allow spending coins on in-app items.
            </Typography>
          </Box>
          <Switch
            checked={enableConsumablePurchases}
            onChange={() => setEnableConsumablePurchases(!enableConsumablePurchases)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: ACCENT,
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                bgcolor: ACCENT,
              },
            }}
          />
        </Box>
      </Box>

      {/* Save Button */}
      <Box sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          variant="contained"
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1.2,
            fontSize: '0.9rem',
            bgcolor: ACCENT,
            color: '#fff',
            boxShadow: `0 4px 14px rgba(109, 83, 244, 0.35)`,
            '&:hover': {
              bgcolor: ACCENT_HOVER,
              boxShadow: `0 6px 20px rgba(109, 83, 244, 0.45)`,
            },
          }}
          onClick={handleSaveSettings}
          disabled={savingSettings}
        >
          {savingSettings ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Paper>
  );

  // ─── Render Coin Packages Tab ───
  const renderCoinPackagesTab = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Coin Packages Section */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #F3F4F6',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>
              Coin Packages
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
              Manage purchasable LBK coin bundles.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddPackageModal(true)}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 0.8,
              fontSize: '0.85rem',
              bgcolor: ACCENT,
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: ACCENT_HOVER,
                boxShadow: 'none',
              },
            }}
          >
            Add Package
          </Button>
        </Box>
        <TableContainer>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={thStyle}>Package Name</TableCell>
                <TableCell sx={thStyle}>Coins</TableCell>
                <TableCell sx={thStyle}>Price</TableCell>
                <TableCell sx={thStyle}>Bonus Coins</TableCell>
                <TableCell sx={thStyle}>Popularity</TableCell>
                <TableCell sx={thStyle}>Status</TableCell>
                <TableCell sx={thStyle}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coinPackages.map((pkg) => (
                <TableRow key={pkg.id} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: '#F9FAFB' } }}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>
                      {pkg.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: '#374151', fontSize: '0.9rem' }}>
                      {pkg.coins}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: '#374151', fontSize: '0.9rem' }}>
                      {pkg.price}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.9rem' }}>
                      {pkg.bonusCoins}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {pkg.popularity ? (
                      <Chip
                        label={pkg.popularity}
                        size="small"
                        sx={{
                          bgcolor: pkg.popularity === 'Popular' ? '#EBF5FF' : pkg.popularity === 'Best Value' ? '#ECFDF5' : pkg.popularity === 'Premium' ? '#F3E8FF' : '#F3F4F6',
                          color: pkg.popularity === 'Popular' ? '#3B82F6' : pkg.popularity === 'Best Value' ? '#10B981' : pkg.popularity === 'Premium' ? '#A855F7' : '#6B7280',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: 24,
                          borderRadius: '6px',
                        }}
                      />
                    ) : (
                      <Typography sx={{ color: '#9CA3AF' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pkg.status}
                      size="small"
                      sx={{
                        bgcolor: pkg.status === 'Active' ? '#EAFDF4' : '#F3F4F6',
                        color: pkg.status === 'Active' ? '#10B981' : '#6B7280',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        borderRadius: '6px',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditPackage(pkg)}
                        sx={{ color: '#9CA3AF', '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT } }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenPkgMenu(e, pkg)}
                        sx={{ color: '#9CA3AF', '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT } }}
                      >
                        <MoreIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Consumable Purchases Section */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #F3F4F6',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>
              Consumable Purchases
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
              Manage items users can buy using LBK Coins.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddConsumableModal(true)}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 0.8,
              fontSize: '0.85rem',
              bgcolor: ACCENT,
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: ACCENT_HOVER,
                boxShadow: 'none',
              },
            }}
          >
            Add Configuration
          </Button>
        </Box>
        <TableContainer>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={thStyle}>Item</TableCell>
                <TableCell sx={thStyle}>Coins Required</TableCell>
                <TableCell sx={thStyle}>Description</TableCell>
                <TableCell sx={thStyle}>Status</TableCell>
                <TableCell sx={thStyle} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingConsumables && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#6B7280' }}>
                    Loading consumables...
                  </TableCell>
                </TableRow>
              )}
              {!loadingConsumables && consumables.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#6B7280' }}>
                    No consumable pricing configured. Click "Add Configuration" to create one.
                  </TableCell>
                </TableRow>
              )}
              {!loadingConsumables && consumables.length > 0 && consumables.map((cons) => (
                <TableRow key={cons._id} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: '#F9FAFB' } }}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>
                      {cons.name}
                    </Typography>
                    <Typography sx={{ color: '#9CA3AF', fontSize: '0.72rem' }}>
                      {cons.itemType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${cons.coinsRequired} Coins`}
                      size="small"
                      sx={{
                        bgcolor: '#F3E8FF',
                        color: '#9333EA',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        borderRadius: '6px',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.9rem' }}>
                      {cons.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={cons.status}
                      size="small"
                      sx={{
                        bgcolor: cons.status === 'Active' ? '#EAFDF4' : '#F3F4F6',
                        color: cons.status === 'Active' ? '#10B981' : '#6B7280',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        borderRadius: '6px',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditConsumable(cons)}
                        sx={{ color: '#9CA3AF', '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT } }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteConsumableClick(cons)}
                        sx={{ color: '#9CA3AF', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleToggleConsumableStatus(cons)}
                        sx={{
                          ml: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          borderRadius: '6px',
                          color: cons.status === 'Active' ? '#6B7280' : '#10B981',
                          borderColor: cons.status === 'Active' ? '#E5E7EB' : '#10B981',
                          '&:hover': {
                            borderColor: cons.status === 'Active' ? '#D1D5DB' : '#059669',
                            bgcolor: cons.status === 'Active' ? '#F9FAFB' : '#F0FDF4',
                          }
                        }}
                      >
                        {cons.status === 'Active' ? 'Disable' : 'Enable'}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  // ─── Render Earning Tasks Tab ───
  const renderEarningTasksTab = () => (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #F3F4F6',
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}
          >
            Coin Earning Tasks
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
            Manage reward actions available to users.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={() => setOpenAddTaskModal(true)}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2,
            py: 1,
            fontSize: '0.82rem',
            bgcolor: ACCENT,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: ACCENT_HOVER,
              boxShadow: '0 2px 8px rgba(109, 83, 244, 0.3)',
            },
          }}
        >
          Add Task
        </Button>
      </Box>

      {/* Tasks Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={thStyle}>Task Name</TableCell>
              <TableCell sx={thStyle}>Reward Coins</TableCell>
              <TableCell sx={thStyle}>Requirement</TableCell>
              <TableCell sx={thStyle}>Status</TableCell>
              <TableCell sx={thStyle}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingEarningTasks ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#6B7280' }}>
                  Loading tasks...
                </TableCell>
              </TableRow>
            ) : earningTasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#6B7280' }}>
                  No earning tasks found. Click "Add Task" to create one.
                </TableCell>
              </TableRow>
            ) : (
              earningTasks.map((task) => (
                <TableRow
                  key={task._id}
                  sx={{
                    '&:hover': { bgcolor: '#FAFAFE' },
                    '& td': {
                      borderBottom: '1px solid #F3F4F6',
                      py: 2,
                      fontSize: '0.875rem',
                    },
                  }}
                >
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: '#111827' }}>
                      {task.name}
                    </Typography>
                    <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>
                      Type: {task.taskType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`+${task.rewardCoins} coins`}
                      size="small"
                      sx={{
                        bgcolor: '#F0FDF4',
                        color: '#16A34A',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 26,
                        borderRadius: '6px',
                        border: 'none',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.875rem' }}>
                      {task.requirement}
                    </Typography>
                    <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>
                      Target: {task.targetCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        color: task.status === 'Active' ? '#16A34A' : '#9CA3AF',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                      }}
                    >
                      {task.status}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditTask(task)}
                        sx={{
                          color: '#9CA3AF',
                          '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenTaskMenu(e, task)}
                        sx={{
                          color: '#9CA3AF',
                          '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT },
                        }}
                      >
                        <MoreIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  // ─── Render Rewards Tab ───
  const renderRewardsTab = () => (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #F3F4F6',
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}
          >
            Reward System
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
            Manage achievements and milestone rewards.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={() => setOpenAddRewardModal(true)}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2,
            py: 1,
            fontSize: '0.82rem',
            bgcolor: ACCENT,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: ACCENT_HOVER,
              boxShadow: '0 2px 8px rgba(109, 83, 244, 0.3)',
            },
          }}
        >
          Add Reward
        </Button>
      </Box>

      {/* Rewards Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={thStyle}>Reward Name</TableCell>
              <TableCell sx={thStyle}>Description</TableCell>
              <TableCell sx={thStyle}>Reward Type</TableCell>
              <TableCell sx={thStyle}>Reward Value</TableCell>
              <TableCell sx={thStyle}>Users Earned</TableCell>
              <TableCell sx={thStyle}>Status</TableCell>
              <TableCell sx={thStyle}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingRewards ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3, color: '#6B7280' }}>Loading rewards...</TableCell>
              </TableRow>
            ) : rewards.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3, color: '#6B7280' }}>No rewards found.</TableCell>
              </TableRow>
            ) : 
              rewards.map((reward) => (
                <TableRow
                  key={reward._id}
                  sx={{
                    '&:hover': { bgcolor: '#FAFAFE' },
                    '& td': {
                      borderBottom: '1px solid #F3F4F6',
                      py: 2,
                      fontSize: '0.875rem',
                    },
                  }}
                >
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: '#111827' }}>
                      {reward.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.82rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {reward.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={reward.rewardType}
                      size="small"
                      sx={{
                        bgcolor: '#F0EDFE',
                        color: ACCENT,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 26,
                        borderRadius: '6px',
                        border: 'none',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.875rem' }}>
                      {reward.value} {reward.unit}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: '#111827' }}>
                      {reward.usersEarnedCount || 0}
                    </Typography>
                  </TableCell>
                <TableCell>
                  <Chip
                    label={reward.status}
                    size="small"
                    sx={{
                      bgcolor: '#EAFDF4',
                      color: '#10B981',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 26,
                      borderRadius: '6px',
                      border: 'none',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditRewardModal(reward)}
                      sx={{
                        color: '#9CA3AF',
                        '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenRewardMenu(e, reward)}
                      sx={{
                        color: '#9CA3AF',
                        '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT },
                      }}
                    >
                      <MoreIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Reward Context Menu */}
      <Menu
        anchorEl={rewardMenuAnchor}
        open={Boolean(rewardMenuAnchor)}
        onClose={handleCloseRewardMenu}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #F3F4F6',
            minWidth: 170,
            py: 0.5,
          },
        }}
      >
        <MenuItem
          onClick={handleDuplicateReward}
          sx={{
            py: 1.2,
            px: 2,
            fontSize: '0.875rem',
            color: '#374151',
            '&:hover': { bgcolor: '#F9FAFB' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <DuplicateIcon sx={{ fontSize: 18, color: '#6B7280' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}>
            Duplicate
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleToggleRewardStatus}
          sx={{
            py: 1.2,
            px: 2,
            fontSize: '0.875rem',
            color: '#374151',
            '&:hover': { bgcolor: '#F9FAFB' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <DeactivateIcon sx={{ fontSize: 18, color: '#6B7280' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}>
            {menuReward?.status === 'Active' ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleDeleteRewardClick}
          sx={{
            py: 1.2,
            px: 2,
            fontSize: '0.875rem',
            color: '#EF4444',
            '&:hover': { bgcolor: '#FEF2F2' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <DeleteIcon sx={{ fontSize: 18, color: '#EF4444' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: '#EF4444' }}>
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );

  const renderWalletAnalyticsTab = () => (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3, mb: 3 }}>
        {/* Chart 1 */}
        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>Coin Purchase Trends</Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>Monthly coin volume and revenue</Typography>
          <Box sx={{ height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData.monthlyStats}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} tickFormatter={(value) => `${value >= 1000 ? value / 1000 + 'k' : value}`} dx={-10} />
                <RechartsTooltip />
                <Area type="monotone" dataKey="volume" stroke="#6D53F4" fill="#6D53F4" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        {/* Chart 2 */}
        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>Top Selling Packages</Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>Units sold per package</Typography>
          <Box sx={{ height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData.packageStats} barSize={30}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} dx={-10} />
                <RechartsTooltip />
                <Bar dataKey="unitsSold" fill="#6D53F4" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3, mb: 3 }}>
        {/* Chart 3 */}
        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>Coins Earned vs Spent</Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>Monthly comparison</Typography>
          <Box sx={{ height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData.monthlyStats} barSize={10} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} tickFormatter={(value) => `${value >= 1000 ? value / 1000 + 'k' : value}`} dx={-10} />
                <RechartsTooltip />
                <Legend iconType="square" wrapperStyle={{ fontSize: 12, paddingTop: 20 }} />
                <Bar dataKey="earned" name="Earned" fill="#6D53F4" radius={[4, 4, 0, 0]} />
                <Bar dataKey="spent" name="Spent" fill="#D8B4FE" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        {/* Chart 4 */}
        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>Daily Active Wallet Users</Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>This week</Typography>
          <Box sx={{ height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData.dailyActiveUsers}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} tickFormatter={(value) => `${value >= 1000 ? value / 1000 + 'k' : value}`} dx={-10} />
                <RechartsTooltip />
                <Area type="monotone" dataKey="users" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>

      <Box sx={{ mb: 3 }}>
        {/* Chart 5 */}
        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 3, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>Revenue by Package</Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>Breakdown of total revenue per coin package</Typography>
          <Box sx={{ height: 250, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1, height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData.packageStats}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="revenue"
                    stroke="none"
                  >
                    {chartData.packageStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#6D53F4', '#A78BFA', '#4F46E5', '#4338CA', '#312E81'][index % 5]} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ flex: 1 }}>
              {chartData.packageStats.map((pack, i) => (
                <Box key={pack.name} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ['#6D53F4', '#A78BFA', '#4F46E5', '#4338CA', '#312E81'][i % 5], mr: 1.5 }} />
                  <Typography variant="body2" sx={{ flex: 1, color: '#374151' }}>{pack.name}</Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500 }}>₹{pack.revenue.toLocaleString()}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );

  // ─── Render placeholder for other tabs ───
  const renderPlaceholderTab = (tabName) => (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        p: 6,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: ACCENT_LIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: '2rem' }}>
          {tabName === 'Coin Packages'
            ? '🪙'
            : tabName === 'Earning Tasks'
            ? '📋'
            : tabName === 'Rewards'
            ? '🎁'
            : '📊'}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
        {tabName}
      </Typography>
      <Typography variant="body2" sx={{ color: '#6B7280', maxWidth: 400, mx: 'auto' }}>
        {tabName === 'Coin Packages' &&
          'Create and manage coin packages available for purchase by users.'}
        {tabName === 'Earning Tasks' &&
          'Set up tasks that users can complete to earn coins and rewards.'}
        {tabName === 'Rewards' &&
          'Configure the reward catalog where users can spend their earned coins.'}
        {tabName === 'Wallet Analytics' &&
          'View detailed analytics on coin circulation, revenue, and user engagement.'}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: '#9CA3AF', mt: 2, fontStyle: 'italic', fontSize: '0.78rem' }}
      >
        Coming soon
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* ─── Country / Currency Row ─── */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: '#374151', mb: 0.5, fontSize: '0.82rem' }}
          >
            Country
          </Typography>
          <FormControl size="small">
            <Select
              value={country}
              onChange={handleCountryChange}
              sx={{
                minWidth: 160,
                borderRadius: '8px',
                bgcolor: '#F9FAFB',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
              }}
            >
              {COUNTRIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: '#374151', mb: 0.5, fontSize: '0.82rem' }}
          >
            Currency
          </Typography>
          <FormControl size="small">
            <Select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              sx={{
                minWidth: 160,
                borderRadius: '8px',
                bgcolor: '#F9FAFB',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
              }}
            >
              {Object.values(CURRENCY_MAP).map((cur) => (
                <MenuItem key={cur} value={cur}>
                  {cur}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>
            Currency auto-fills based on country selection.
          </Typography>
        </Box>
      </Box>

      {/* ─── Page Header ─── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#111827', fontSize: '1.5rem' }}
          >
            LBK Wallet Management
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
            Manage coin packages, rewards, earning tasks and wallet settings.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddPackageModal(true)}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1.2,
            fontSize: '0.9rem',
            bgcolor: ACCENT,
            color: '#fff',
            boxShadow: `0 4px 14px rgba(109, 83, 244, 0.35)`,
            '&:hover': {
              bgcolor: ACCENT_HOVER,
              boxShadow: `0 6px 20px rgba(109, 83, 244, 0.45)`,
            },
          }}
        >
          Create Coin Package
        </Button>
      </Box>

      {/* ─── Stats Cards ─── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {stats.map((stat) => (
          <Paper
            key={stat.label}
            elevation={0}
            sx={{
              flex: 1,
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              p: 2.5,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#6B7280',
                  fontSize: '0.78rem',
                  mb: 0.5,
                  whiteSpace: 'pre-line',
                }}
              >
                {stat.label}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '1.75rem',
                  color: '#111827',
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                {stat.value}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {stat.positive ? (
                  <TrendUpIcon sx={{ fontSize: 14, color: '#16A34A' }} />
                ) : (
                  <TrendDownIcon sx={{ fontSize: 14, color: '#DC2626' }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: stat.positive ? '#16A34A' : '#DC2626',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                  }}
                >
                  {stat.change}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: '#9CA3AF', fontSize: '0.72rem' }}
                >
                  vs last month
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: stat.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: stat.iconColor,
                flexShrink: 0,
              }}
            >
              {stat.icon}
            </Box>
          </Paper>
        ))}
      </Box>

      {/* ─── Sub-Tabs ─── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #E5E7EB',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', gap: 0 }}>
          {SUB_TABS.map((tab) => (
            <Button
              key={tab}
              onClick={() => setActiveTab(tab)}
              sx={{
                textTransform: 'none',
                fontWeight: activeTab === tab ? 700 : 500,
                fontSize: '0.9rem',
                px: 2.5,
                py: 1.5,
                borderRadius: 0,
                color: activeTab === tab ? ACCENT : '#6B7280',
                borderBottom:
                  activeTab === tab
                    ? `2.5px solid ${ACCENT}`
                    : '2.5px solid transparent',
                '&:hover': {
                  bgcolor: 'transparent',
                  color: ACCENT,
                },
                transition: 'all 0.2s ease',
              }}
            >
              {tab}
            </Button>
          ))}
        </Box>
      </Box>

      {/* ─── Tab Content ─── */}
      {activeTab === 'Coin Packages' && renderCoinPackagesTab()}
      {activeTab === 'Settings' && renderSettingsTab()}
      {activeTab === 'Earning Tasks' && renderEarningTasksTab()}
      {activeTab === 'Rewards' && renderRewardsTab()}
      {activeTab === 'Wallet Analytics' && renderWalletAnalyticsTab()}
      {!['Coin Packages', 'Settings', 'Earning Tasks', 'Rewards', 'Wallet Analytics'].includes(activeTab) && renderPlaceholderTab(activeTab)}

      {/* ─── Add Reward Dialog ─── */}
      <Dialog
        open={openAddRewardModal}
        onClose={handleCloseRewardModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 3, pt: 3, pb: 2, borderBottom: '1px solid #F3F4F6' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>
              Add Reward
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 0.2 }}>
              Create a new milestone reward.
            </Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={handleCloseRewardModal}
            sx={{ color: '#9CA3AF', '&:hover': { color: '#4B5563' } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent sx={{ px: 3, py: 2.5, pb: 2 }}>
          {/* Reward Name */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
              Reward Name <span style={{ color: '#EF4444' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. Profile Verification"
              value={newRewardName}
              onChange={(e) => setNewRewardName(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '0.875rem',
                }
              }}
            />
          </Box>

          {/* Reward Description */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
              Description
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. Reward given upon completing profile verification"
              value={newRewardDesc}
              onChange={(e) => setNewRewardDesc(e.target.value)}
              size="small"
              multiline
              rows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '0.875rem',
                }
              }}
            />
          </Box>

          {/* Row 2: Reward Type & Reward Value */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
                Reward Type
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. Free Connect"
                value={newRewardType}
                onChange={(e) => setNewRewardType(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: ACCENT },
                    '&.Mui-focused fieldset': { borderColor: ACCENT },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
                Reward Value <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. 5"
                value={newRewardValue}
                onChange={(e) => setNewRewardValue(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: ACCENT },
                    '&.Mui-focused fieldset': { borderColor: ACCENT },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
                Unit <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. Days or Coins"
                value={newRewardUnit}
                onChange={(e) => setNewRewardUnit(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: ACCENT },
                    '&.Mui-focused fieldset': { borderColor: ACCENT },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Box>
          </Box>

          {/* Status */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
              Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Active Button */}
              <Box
                onClick={() => setNewRewardStatus('Active')}
                sx={{
                  flex: 1,
                  py: 1.2,
                  textAlign: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  border: newRewardStatus === 'Active' ? '1px solid #10B981' : '1px solid #E5E7EB',
                  bgcolor: newRewardStatus === 'Active' ? '#EAFDF4' : '#F9FAFB',
                  color: newRewardStatus === 'Active' ? '#10B981' : '#9CA3AF',
                  transition: 'all 0.2s',
                  '&:hover': {
                    border: newRewardStatus === 'Active' ? '1px solid #10B981' : '1px solid #D1D5DB',
                  }
                }}
              >
                Active
              </Box>

              {/* Inactive Button */}
              <Box
                onClick={() => setNewRewardStatus('Inactive')}
                sx={{
                  flex: 1,
                  py: 1.2,
                  textAlign: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  border: newRewardStatus === 'Inactive' ? '1px solid #9CA3AF' : '1px solid #E5E7EB',
                  bgcolor: newRewardStatus === 'Inactive' ? '#F3F4F6' : '#F9FAFB',
                  color: newRewardStatus === 'Inactive' ? '#4B5563' : '#9CA3AF',
                  transition: 'all 0.2s',
                  '&:hover': {
                    border: newRewardStatus === 'Inactive' ? '1px solid #9CA3AF' : '1px solid #D1D5DB',
                  }
                }}
              >
                Inactive
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button
            onClick={handleCloseRewardModal}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              fontSize: '0.875rem',
              color: '#374151',
              border: '1px solid #E5E7EB',
              bgcolor: '#fff',
              '&:hover': {
                bgcolor: '#F9FAFB',
                borderColor: '#D1D5DB',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddRewardSubmit}
            variant="contained"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              fontSize: '0.875rem',
              bgcolor: ACCENT,
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: ACCENT_HOVER,
                boxShadow: 'none',
              },
            }}
          >
            Add Reward
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Edit Reward Dialog ─── */}
      <Dialog
        open={openEditRewardModal}
        onClose={handleCloseEditRewardModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 3, pt: 3, pb: 2, borderBottom: '1px solid #F3F4F6' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>
              Edit Reward
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 0.2 }}>
              Edit an existing milestone reward.
            </Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={handleCloseEditRewardModal}
            sx={{ color: '#9CA3AF', '&:hover': { color: '#4B5563' } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent sx={{ px: 3, py: 2.5, pb: 2 }}>
          {/* Reward Name */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
              Reward Name <span style={{ color: '#EF4444' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. Profile Verification"
              value={editRewardName}
              onChange={(e) => setEditRewardName(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '0.875rem',
                }
              }}
            />
          </Box>

          {/* Reward Description */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
              Description
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. Reward given upon completing profile verification"
              value={editRewardDesc}
              onChange={(e) => setEditRewardDesc(e.target.value)}
              size="small"
              multiline
              rows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '0.875rem',
                }
              }}
            />
          </Box>

          {/* Row 2: Reward Type & Reward Value */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
                Reward Type
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. Free Connect"
                value={editRewardType}
                onChange={(e) => setEditRewardType(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: ACCENT },
                    '&.Mui-focused fieldset': { borderColor: ACCENT },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
                Reward Value <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. 5"
                value={editRewardValue}
                onChange={(e) => setEditRewardValue(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: ACCENT },
                    '&.Mui-focused fieldset': { borderColor: ACCENT },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
                Unit <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. Days or Coins"
                value={editRewardUnit}
                onChange={(e) => setEditRewardUnit(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: ACCENT },
                    '&.Mui-focused fieldset': { borderColor: ACCENT },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Box>
          </Box>

          {/* Status */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
              Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Active Button */}
              <Box
                onClick={() => setEditRewardStatus('Active')}
                sx={{
                  flex: 1,
                  py: 1.2,
                  textAlign: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  border: editRewardStatus === 'Active' ? '1px solid #10B981' : '1px solid #E5E7EB',
                  bgcolor: editRewardStatus === 'Active' ? '#EAFDF4' : '#F9FAFB',
                  color: editRewardStatus === 'Active' ? '#10B981' : '#9CA3AF',
                  transition: 'all 0.2s',
                  '&:hover': {
                    border: editRewardStatus === 'Active' ? '1px solid #10B981' : '1px solid #D1D5DB',
                  }
                }}
              >
                Active
              </Box>

              {/* Inactive Button */}
              <Box
                onClick={() => setEditRewardStatus('Inactive')}
                sx={{
                  flex: 1,
                  py: 1.2,
                  textAlign: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  border: editRewardStatus === 'Inactive' ? '1px solid #9CA3AF' : '1px solid #E5E7EB',
                  bgcolor: editRewardStatus === 'Inactive' ? '#F3F4F6' : '#F9FAFB',
                  color: editRewardStatus === 'Inactive' ? '#4B5563' : '#9CA3AF',
                  transition: 'all 0.2s',
                  '&:hover': {
                    border: editRewardStatus === 'Inactive' ? '1px solid #9CA3AF' : '1px solid #D1D5DB',
                  }
                }}
              >
                Inactive
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button
            onClick={handleCloseEditRewardModal}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              fontSize: '0.875rem',
              color: '#374151',
              border: '1px solid #E5E7EB',
              bgcolor: '#fff',
              '&:hover': {
                bgcolor: '#F9FAFB',
                borderColor: '#D1D5DB',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditRewardSubmit}
            variant="contained"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              fontSize: '0.875rem',
              bgcolor: ACCENT,
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: ACCENT_HOVER,
                boxShadow: 'none',
              },
            }}
          >
            Edit Reward
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Add Coin Package Dialog ─── */}
      <Dialog open={openAddPackageModal} onClose={handleClosePackageModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>Create Coin Package</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 0.2 }}>Add a new purchasable LBK coin bundle.</Typography>
          </Box>
          <IconButton size="small" onClick={handleClosePackageModal} sx={{ color: '#9CA3AF' }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5, pb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Package Name <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. Starter Pack" value={pkgName} onChange={(e) => setPkgName(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Coins <span style={{ color: '#EF4444' }}>*</span></Typography>
              <TextField fullWidth placeholder="e.g. 1,200" value={pkgCoins} onChange={(e) => setPkgCoins(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Price <span style={{ color: '#EF4444' }}>*</span></Typography>
              <TextField fullWidth placeholder="e.g. ₹199" value={pkgPrice} onChange={(e) => setPkgPrice(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Bonus Coins</Typography>
              <TextField fullWidth placeholder="0" value={pkgBonus} onChange={(e) => setPkgBonus(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Popularity</Typography>
              <TextField fullWidth placeholder="" value={pkgPopularity} onChange={(e) => setPkgPopularity(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Status</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box onClick={() => setPkgStatus('Active')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: pkgStatus === 'Active' ? '1px solid #10B981' : '1px solid #E5E7EB', bgcolor: pkgStatus === 'Active' ? '#EAFDF4' : '#F9FAFB', color: pkgStatus === 'Active' ? '#10B981' : '#9CA3AF' }}>Active</Box>
              <Box onClick={() => setPkgStatus('Inactive')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: pkgStatus === 'Inactive' ? '1px solid #9CA3AF' : '1px solid #E5E7EB', bgcolor: pkgStatus === 'Inactive' ? '#F3F4F6' : '#F9FAFB', color: pkgStatus === 'Inactive' ? '#4B5563' : '#9CA3AF' }}>Inactive</Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={handleClosePackageModal} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', color: '#374151', border: '1px solid #E5E7EB' }}>Cancel</Button>
          <Button onClick={handleSavePackage} variant="contained" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', bgcolor: ACCENT, color: '#fff' }}>Create Package</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Edit Coin Package Dialog ─── */}
      <Dialog open={openEditPackageModal} onClose={handleClosePackageModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>Edit Coin Package</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 0.2 }}>Update this LBK coin bundle.</Typography>
          </Box>
          <IconButton size="small" onClick={handleClosePackageModal} sx={{ color: '#9CA3AF' }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5, pb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Package Name <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. Starter Pack" value={pkgName} onChange={(e) => setPkgName(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Coins <span style={{ color: '#EF4444' }}>*</span></Typography>
              <TextField fullWidth placeholder="e.g. 1,200" value={pkgCoins} onChange={(e) => setPkgCoins(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Price <span style={{ color: '#EF4444' }}>*</span></Typography>
              <TextField fullWidth placeholder="e.g. ₹199" value={pkgPrice} onChange={(e) => setPkgPrice(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Bonus Coins</Typography>
              <TextField fullWidth placeholder="0" value={pkgBonus} onChange={(e) => setPkgBonus(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Popularity</Typography>
              <TextField fullWidth placeholder="" value={pkgPopularity} onChange={(e) => setPkgPopularity(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Status</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box onClick={() => setPkgStatus('Active')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: pkgStatus === 'Active' ? '1px solid #10B981' : '1px solid #E5E7EB', bgcolor: pkgStatus === 'Active' ? '#EAFDF4' : '#F9FAFB', color: pkgStatus === 'Active' ? '#10B981' : '#9CA3AF' }}>Active</Box>
              <Box onClick={() => setPkgStatus('Inactive')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: pkgStatus === 'Inactive' ? '1px solid #9CA3AF' : '1px solid #E5E7EB', bgcolor: pkgStatus === 'Inactive' ? '#F3F4F6' : '#F9FAFB', color: pkgStatus === 'Inactive' ? '#4B5563' : '#9CA3AF' }}>Inactive</Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={handleClosePackageModal} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', color: '#374151', border: '1px solid #E5E7EB' }}>Cancel</Button>
          <Button onClick={handleSavePackage} variant="contained" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', bgcolor: ACCENT, color: '#fff' }}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Add Consumable Dialog ─── */}
      <Dialog open={openAddConsumableModal} onClose={handleCloseConsumableModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>Add Configuration</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 0.2 }}>Add a new coin-purchasable in-app item.</Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseConsumableModal} sx={{ color: '#9CA3AF' }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5, pb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Item Name <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. Super Like" value={consName} onChange={(e) => setConsName(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Item Type (Unique ID) <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. super_like" value={consItemType} onChange={(e) => setConsItemType(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>Lowercase identifier used by the app (e.g. super_like, profile_boost).</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Coins Required <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. 15" value={consCoins} onChange={(e) => setConsCoins(e.target.value)} size="small" InputProps={{ endAdornment: <Typography sx={{ color: '#9CA3AF', fontSize: '0.875rem' }}>coins</Typography> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Description <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. Boost profile reach" value={consDesc} onChange={(e) => setConsDesc(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Status</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box onClick={() => setConsStatus('Active')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: consStatus === 'Active' ? '1px solid #10B981' : '1px solid #E5E7EB', bgcolor: consStatus === 'Active' ? '#EAFDF4' : '#F9FAFB', color: consStatus === 'Active' ? '#10B981' : '#9CA3AF' }}>Active</Box>
              <Box onClick={() => setConsStatus('Disabled')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: consStatus === 'Disabled' ? '1px solid #9CA3AF' : '1px solid #E5E7EB', bgcolor: consStatus === 'Disabled' ? '#F3F4F6' : '#F9FAFB', color: consStatus === 'Disabled' ? '#4B5563' : '#9CA3AF' }}>Disabled</Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={handleCloseConsumableModal} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', color: '#374151', border: '1px solid #E5E7EB' }}>Cancel</Button>
          <Button onClick={handleSaveConsumable} variant="contained" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', bgcolor: ACCENT, color: '#fff' }}>Add Configuration</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Edit Consumable Dialog ─── */}
      <Dialog open={openEditConsumableModal} onClose={handleCloseConsumableModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>Edit Consumable</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 0.2 }}>Update this purchasable item.</Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseConsumableModal} sx={{ color: '#9CA3AF' }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5, pb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Item Name <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. Super Like" value={consName} onChange={(e) => setConsName(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Item Type (Unique ID) <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. super_like" value={consItemType} onChange={(e) => setConsItemType(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>Lowercase identifier used by the app (e.g. super_like, profile_boost).</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Coins Required <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. 15" value={consCoins} onChange={(e) => setConsCoins(e.target.value)} size="small" type="number" InputProps={{ endAdornment: <Typography sx={{ color: '#9CA3AF', fontSize: '0.875rem' }}>coins</Typography> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Description</Typography>
            <TextField fullWidth placeholder="e.g. Boost profile reach" value={consDesc} onChange={(e) => setConsDesc(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Status</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box onClick={() => setConsStatus('Active')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: consStatus === 'Active' ? '1px solid #10B981' : '1px solid #E5E7EB', bgcolor: consStatus === 'Active' ? '#EAFDF4' : '#F9FAFB', color: consStatus === 'Active' ? '#10B981' : '#9CA3AF' }}>Active</Box>
              <Box onClick={() => setConsStatus('Disabled')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: consStatus === 'Disabled' ? '1px solid #9CA3AF' : '1px solid #E5E7EB', bgcolor: consStatus === 'Disabled' ? '#F3F4F6' : '#F9FAFB', color: consStatus === 'Disabled' ? '#4B5563' : '#9CA3AF' }}>Disabled</Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={handleCloseConsumableModal} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', color: '#374151', border: '1px solid #E5E7EB' }}>Cancel</Button>
          <Button onClick={handleSaveConsumable} variant="contained" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', bgcolor: ACCENT, color: '#fff' }}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Delete Consumable Confirmation Dialog ─── */}
      <Dialog
        open={openDeleteConsumableDialog}
        onClose={handleCancelDeleteConsumable}
        disableScrollLock={true}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
            minWidth: 320,
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 3, px: 3 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#374151' }}>
            Delete "{deletingConsumable?.name}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2.5, gap: 1.5 }}>
          <Button
            onClick={handleCancelDeleteConsumable}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 0.8,
              fontSize: '0.875rem',
              color: '#374151',
              border: '1px solid #E5E7EB',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteConsumable}
            variant="contained"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 0.8,
              fontSize: '0.875rem',
              bgcolor: '#EF4444',
              color: '#fff',
              '&:hover': { bgcolor: '#DC2626' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Coin Package Context Menu ─── */}
      <Menu
        anchorEl={pkgMenuAnchor}
        open={Boolean(pkgMenuAnchor)}
        onClose={() => { setPkgMenuAnchor(null); setMenuPackage(null); }}
        disableScrollLock={true}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #F3F4F6',
            minWidth: 160,
            py: 0.5,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleDuplicatePackage} sx={{ py: 1.2, px: 2, fontSize: '0.875rem', gap: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 'unset' }}>
            <DuplicateIcon sx={{ fontSize: 18, color: '#6B7280' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
            Duplicate
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleTogglePackageStatus} sx={{ py: 1.2, px: 2, fontSize: '0.875rem', gap: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 'unset' }}>
            {menuPackage?.status === 'Active'
              ? <DeactivateIcon sx={{ fontSize: 18, color: '#6B7280' }} />
              : <ActivateIcon sx={{ fontSize: 18, color: '#10B981' }} />
            }
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: menuPackage?.status === 'Active' ? '#374151' : '#10B981' }}>
            {menuPackage?.status === 'Active' ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeletePackageClick} sx={{ py: 1.2, px: 2, fontSize: '0.875rem', gap: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 'unset' }}>
            <DeleteIcon sx={{ fontSize: 18, color: '#EF4444' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: '#EF4444' }}>
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* ─── Delete Reward Confirmation Dialog ─── */}
      <Dialog
        open={openDeleteRewardDialog}
        onClose={handleCancelDeleteReward}
        disableScrollLock={true}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
            minWidth: 320,
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 3, px: 3 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#374151' }}>
            Delete "{deletingReward?.name}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2.5, gap: 1.5 }}>
          <Button
            onClick={handleCancelDeleteReward}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 0.8,
              fontSize: '0.875rem',
              color: '#374151',
              border: '1px solid #E5E7EB',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteReward}
            variant="contained"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 0.8,
              fontSize: '0.875rem',
              bgcolor: '#EF4444',
              color: '#fff',
              '&:hover': { bgcolor: '#DC2626' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Delete Coin Package Confirmation Dialog ─── */}
      <Dialog
        open={openDeletePkgDialog}
        onClose={handleCancelDeletePackage}
        disableScrollLock={true}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
            minWidth: 320,
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 3, px: 3 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#374151' }}>
            Delete "{deletingPackage?.name}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2.5, gap: 1.5 }}>
          <Button
            onClick={handleCancelDeletePackage}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 0.8,
              fontSize: '0.875rem',
              color: '#374151',
              border: '1px solid #E5E7EB',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeletePackage}
            variant="contained"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 0.8,
              fontSize: '0.875rem',
              bgcolor: '#EF4444',
              color: '#fff',
              '&:hover': { bgcolor: '#DC2626' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* ─── Earning Task Add/Edit Modal ─── */}
      <Dialog
        open={openAddTaskModal || openEditTaskModal}
        onClose={handleCloseTaskModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>
              {openEditTaskModal ? 'Edit Task' : 'Create Task'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 0.2 }}>
              {openEditTaskModal ? 'Update an existing earning task.' : 'Add a new action for users to earn coins.'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseTaskModal} sx={{ color: '#9CA3AF' }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5, pb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
              Task Name <span style={{ color: '#EF4444' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. Connect With 5 New People"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
                Task Type <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. USER_CONNECT"
                value={taskType}
                onChange={(e) => setTaskType(e.target.value)}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
                Reward Coins <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. 20"
                type="number"
                value={taskReward}
                onChange={(e) => setTaskReward(e.target.value)}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
                Requirement <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. Make 5 connections"
                value={taskRequirement}
                onChange={(e) => setTaskRequirement(e.target.value)}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
                Target Count <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="1"
                type="number"
                value={taskTargetCount}
                onChange={(e) => setTaskTargetCount(e.target.value)}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }}
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
              Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box onClick={() => setTaskStatus('Active')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: taskStatus === 'Active' ? '1px solid #10B981' : '1px solid #E5E7EB', bgcolor: taskStatus === 'Active' ? '#EAFDF4' : '#F9FAFB', color: taskStatus === 'Active' ? '#10B981' : '#9CA3AF' }}>Active</Box>
              <Box onClick={() => setTaskStatus('Inactive')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: taskStatus === 'Inactive' ? '1px solid #9CA3AF' : '1px solid #E5E7EB', bgcolor: taskStatus === 'Inactive' ? '#F3F4F6' : '#F9FAFB', color: taskStatus === 'Inactive' ? '#4B5563' : '#9CA3AF' }}>Inactive</Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={handleCloseTaskModal} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', color: '#374151', border: '1px solid #E5E7EB' }}>Cancel</Button>
          <Button onClick={handleSaveTask} variant="contained" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', bgcolor: ACCENT, color: '#fff' }}>Save Task</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Earning Task Context Menu ─── */}
      <Menu
        anchorEl={taskMenuAnchor}
        open={Boolean(taskMenuAnchor)}
        onClose={handleCloseTaskMenu}
        disableScrollLock={true}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #F3F4F6',
            minWidth: 160,
            py: 0.5,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleToggleTaskStatus} sx={{ py: 1.2, px: 2, fontSize: '0.875rem', gap: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 'unset' }}>
            {menuTask?.status === 'Active'
              ? <DeactivateIcon sx={{ fontSize: 18, color: '#6B7280' }} />
              : <ActivateIcon sx={{ fontSize: 18, color: '#10B981' }} />
            }
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: menuTask?.status === 'Active' ? '#374151' : '#10B981' }}>
            {menuTask?.status === 'Active' ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteTaskClick} sx={{ py: 1.2, px: 2, fontSize: '0.875rem', gap: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 'unset' }}>
            <DeleteIcon sx={{ fontSize: 18, color: '#EF4444' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: '#EF4444' }}>
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* ─── Delete Task Confirmation Dialog ─── */}
      <Dialog
        open={openDeleteTaskDialog}
        onClose={handleCancelDeleteTask}
        disableScrollLock={true}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
            minWidth: 320,
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 3, px: 3 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#374151' }}>
            Delete "{deletingTask?.name}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2.5, gap: 1.5 }}>
          <Button
            onClick={handleCancelDeleteTask}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 0.8, fontSize: '0.875rem', color: '#374151', border: '1px solid #E5E7EB' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteTask}
            variant="contained"
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 0.8, fontSize: '0.875rem', bgcolor: '#EF4444', color: '#fff', '&:hover': { bgcolor: '#DC2626' } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LBKWalletManagement;
